import React from 'react';
import { useGetProxyUsersQuery, useSearchWebUsersQuery, useSetUserProxyMutation } from '@ssg/common/GraphQL/indexV2';
import Box from 'Components/Layout/Box';
import BoxContainer from '@ssg/common/Components/BoxContainer';
import Table from '@ssg/common/Components/Table';
import { useTranslation } from 'react-i18next';
import dateToDateTimeString from '@ssg/common/Helpers/dateToDateTimeString';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';
import Button from '@ssg/common/Components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { useForm } from 'react-hook-form';
import useDebouncedState from '@ssg/common/Hooks/useDebouncedState';
import SearchableSelect from '@ssg/common/Components/SearchableSelect';
import UserContext from 'UserContext';

const UsersProxyOverview: React.FC = (): React.ReactElement => {
	const { t } = useTranslation();
	const userContext = React.useContext(UserContext);

	const { data, loading, refetch } = useGetProxyUsersQuery();
	const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
	const [showAddMdal, setShowAddModal] = React.useState(false);
	const [postSetUserProxy, { loading: setUserProxyLoading }] = useSetUserProxyMutation();

	const handleSetUserPoxy = async (userId: string, proxyAdUserName = '') => {
		if (userContext.user) {
			await postSetUserProxy({
				variables: {
					myAdUserName: userId,
					proxyAdUserName,
					proxySetByAdUserName: userContext.user.id,
				},
				onCompleted: () => {
					setSelectedUserId(null);
					setShowAddModal(false);
					refetch();
				},
			});
		}
	};

	const dummyForm = useForm();
	const [mySearchText, setMySearchText] = useDebouncedState('', 100);
	const [myProxy, setMyProxy] = React.useState<string>();
	const { loading: myLoading, data: myData } = useSearchWebUsersQuery({
		variables: {
			searchText: mySearchText,
			onlyEmployees: true,
		},
		skip: mySearchText === '',
	});
	const myUsers = React.useMemo(() => myData?.searchUsers.map(u => ({ label: `${u.name}`, value: u.id })) ?? [], [myData]);

	const [proxySearchText, setProxySearchText] = useDebouncedState('', 100);
	const [proxy, setProxy] = React.useState<string>();
	const { loading: proxyLoading, data: proxyData } = useSearchWebUsersQuery({
		variables: {
			searchText: proxySearchText,
			onlyEmployees: true,
		},
		skip: proxySearchText === '',
	});
	const proxyUsers = React.useMemo(() => proxyData?.searchUsers.map(u => ({ label: `${u.name}`, value: u.id })) ?? [], [proxyData]);

	return (
		<>
			<BoxContainer>
				<Box full>
					<div className="flex flex-row justify-end">
						<Button primary text="users.addProxy" icon={faPlus} onClick={() => setShowAddModal(true)} className="mb-2" />
					</div>
					<Table
						data={data?.users ?? []}
						loading={loading}
						columns={[
							{
								label: 'common.name',
								selectFn: c => (
									<div className="py-2">
										<p className="font-semibold">{c.name}</p>
										<p>{c.email}</p>
									</div>
								),
								sortFn: (a, b) => a.name.localeCompare(b.name),
							},
							{
								label: 'users.proxy',
								selectFn: c => (
									<div className="py-2">
										<p className="font-semibold">{c.proxy?.name}</p>
										<p>{c.proxy?.email}</p>
									</div>
								),
								sortFn: (a, b) => (a.proxy?.name ?? '').localeCompare(b.proxy?.name ?? ''),
							},
							{
								label: 'users.updatedBy',
								selectFn: c => (
									<div className="py-2">
										<p className="font-semibold">{c.proxySetBy?.name}</p>
										<p>{dateToDateTimeString(new Date(c.proxyUpdatedAt))}</p>
									</div>
								),
								sortFn: (a, b) => (a.proxySetBy?.name ?? '').localeCompare(b.proxySetBy?.name ?? ''),
							},
							{
								label: '',
								selectFn: c => (
									<div className="text-red flex content-start justify-end text-right pr-4">
										<FontAwesomeIcon
											icon={faTrashAlt}
											size="lg"
											onClick={() => {
												setSelectedUserId(c.id);
											}}
											className="cursor-pointer"
										/>
									</div>
								),
							},
						]}
						keySelector={(c, i) => c.id + i}
					/>
				</Box>
			</BoxContainer>
			{selectedUserId && (
				<Modal
					title="users.removeProxy"
					size={ModalSize.SMALL}
					visible={true}
					close={() => setSelectedUserId(null)}
					body={
						<>
							<p className="mb-2">{t('users.wantToRemoveProxy')}</p>

							<div className="flex justify-between">
								<Button danger loading={setUserProxyLoading} text="users.removeProxy" className="mt-3" onClick={() => handleSetUserPoxy(selectedUserId)} />
								<Button secondary text="common.cancel" className="mt-3" onClick={() => setSelectedUserId(null)} />
							</div>
						</>
					}
				/>
			)}
			{showAddMdal && (
				<Modal
					title="users.addProxy"
					size={ModalSize.SMALL}
					visible={true}
					close={() => setShowAddModal(false)}
					body={
						<>
							<SearchableSelect
								control={dummyForm.control}
								name="myProxy"
								title="common.name"
								options={myUsers ?? []}
								searchFn={(searchText): void => setMySearchText(searchText)}
								onSelect={value => setMyProxy(value)}
								onBlur={(clearFn): void => undefined}
								minInputLength={2}
								isLoading={myLoading}
								required
							/>
							<SearchableSelect
								control={dummyForm.control}
								name="users.proxy"
								title="users.proxy"
								options={proxyUsers ?? []}
								searchFn={(searchText): void => setProxySearchText(searchText)}
								onSelect={value => setProxy(value)}
								onBlur={(clearFn): void => undefined}
								minInputLength={2}
								isLoading={proxyLoading}
								required
							/>
							<div className="flex justify-between">
								<Button
									success
									loading={setUserProxyLoading}
									text="users.addProxy"
									className="mt-3"
									disabled={!myProxy || !proxy}
									onClick={async () => {
										if (myProxy && proxy) {
											await handleSetUserPoxy(myProxy, proxy);
										}
									}}
								/>
								<Button secondary text="common.cancel" className="mt-3" onClick={() => setShowAddModal(false)} />
							</div>
						</>
					}
				/>
			)}
		</>
	);
};

export default UsersProxyOverview;

